import type { AuthResponseDto, RegisterDto } from "@reactive-resume/dto";
import { useMutation } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";

import { axios } from "@/client/libs/axios";
import { queryClient } from "@/client/libs/query-client";
import { useAuthStore } from "@/client/stores/auth";

/**
 * Lit le parrain présent dans l'adresse d'inscription.
 *
 * Exemple :
 * /client/auth/register?referrer=identifiant-du-parrain
 *
 * Cette lecture n'accorde aucun droit :
 * le backend vérifiera l'existence et l'éligibilité du parrain.
 */
const getReferrerFromCurrentUrl = (): string | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  const searchParams = new URLSearchParams(window.location.search);

  const referrer = searchParams.get("referrer")?.trim();

  return referrer || undefined;
};

/**
 * Inscription par e-mail et mot de passe.
 *
 * On conserve la signature existante register(data) :
 * la page d'inscription n'a pas besoin d'être réécrite.
 *
 * Les informations du formulaire restent dans le corps de la requête.
 * Le parrain est transmis en paramètre d'URL, comme attendu
 * par AuthController.register dans le backend actuel.
 */
export const register = async (data: RegisterDto): Promise<AuthResponseDto> => {
  const referrer = getReferrerFromCurrentUrl();

  const response = await axios.post<AuthResponseDto, AxiosResponse<AuthResponseDto>, RegisterDto>(
    "auth/register",
    data,
    {
      params: referrer ? { referrer } : undefined,
    },
  );

  return response.data;
};

/**
 * Conserve le fonctionnement actuel après inscription :
 * - mise à jour du compte connecté ;
 * - mise à jour du cache utilisateur.
 *
 * Ne crée aucun paiement, bonus ou point de fidélité.
 */
export const useRegister = () => {
  const setUser = useAuthStore((state) => state.setUser);

  const {
    error,
    isPending: loading,
    mutateAsync: registerFn,
  } = useMutation({
    mutationFn: register,

    onSuccess: (data) => {
      setUser(data.user);
      queryClient.setQueryData(["user"], data.user);
    },
  });

  return {
    register: registerFn,
    loading,
    error,
  };
};
